import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Clock, Eye, Loader2, Zap } from 'lucide-react';
import { adminClientPaymentsAPI } from '../../services/api';
import { formatMoney, formatDate, formatDateTime } from '../../utils/format';

/*
 * AutoPay charging (Phase 7e): what the next run would charge, per company
 * with AutoPay on, and why any would be skipped — the same plan the job
 * executes. Shown before charging is switched on (so an admin can check it)
 * and after (so they can see what it did). Without a companyId it covers
 * every company the admin may see; with one, just that company.
 */

/** A YYYY-MM-DD is a calendar date, not an instant: read it at noon UTC so no zone moves it. */
export function formatCalendarDate(value) {
  if (!value) return '';
  const day = String(value).slice(0, 10);
  return formatDate(`${day}T12:00:00Z`, { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' });
}

const ACTION_STYLE = {
  charge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  skip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  none: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
};

const RUN_STYLE = {
  succeeded: 'text-green-700 dark:text-green-400',
  failed: 'text-red-700 dark:text-red-400',
  needs_review: 'text-red-700 dark:text-red-400 font-semibold',
  requires_action: 'text-amber-700 dark:text-amber-400',
  pending: 'text-neutral-600 dark:text-neutral-300',
  skipped: 'text-amber-700 dark:text-amber-400',
};

const RUN_LABEL = {
  succeeded: 'Charged',
  failed: 'Not charged',
  needs_review: 'Needs review',
  requires_action: 'Waiting for customer',
  pending: 'In progress',
  skipped: 'Skipped',
};

function actionLabel(entry, chargingEnabled) {
  if (entry.action === 'charge') return chargingEnabled ? 'Will charge' : 'Would charge';
  if (entry.action === 'skip') return 'Skipped';
  return entry.reason === 'not_charge_day' ? 'Not today' : 'Nothing due';
}

function methodText(m) {
  if (!m) return '—';
  const gw = m.gateway === 'authorizenet' ? 'Authorize.net' : m.gateway === 'stripe_connect' ? 'Platform payments' : m.gateway === 'stripe' ? 'Stripe' : m.gateway;
  return `${m.label || `•••• ${m.lastFour || '????'}`} · ${gw}`;
}

function ResolveButtons({ run }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: (data) => adminClientPaymentsAPI.resolveAutopayRun(run.id, data),
    onSuccess: () => {
      setError('');
      queryClient.invalidateQueries({ queryKey: ['autopay-preview'] });
    },
    onError: (err) => setError(err.response?.data?.error || 'Could not resolve the run'),
  });
  const notCharged = () => {
    if (window.confirm('Check the gateway first. Record this as NOT charged? AutoPay will charge these invoices again at its next run.')) {
      mutation.mutate({ outcome: 'not_charged' });
    }
  };
  const charged = () => {
    const txn = window.prompt('The transaction id the gateway shows for this charge. It is checked with the gateway before anything is recorded.', run.transaction_id || '');
    if (txn) mutation.mutate({ outcome: 'charged', transactionId: txn.trim() });
  };
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex gap-2">
        <button type="button" onClick={charged} disabled={mutation.isPending} className="px-2 py-1 text-xs rounded border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700">
          It was charged
        </button>
        <button type="button" onClick={notCharged} disabled={mutation.isPending} className="px-2 py-1 text-xs rounded border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700">
          Not charged
        </button>
      </div>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

export default function AutopayPreviewPanel({ companyId = null, collapsible = false }) {
  const [open, setOpen] = useState(!collapsible);
  const { data, isLoading, error } = useQuery({
    queryKey: ['autopay-preview', companyId || 'all'],
    queryFn: () => adminClientPaymentsAPI.getAutopayPreview(companyId),
    enabled: open,
  });
  const body = data?.data;
  const settings = body?.settings;
  const plan = body?.plan;
  const runs = body?.recentRuns || [];
  const review = runs.filter((r) => r.status === 'needs_review');

  const header = (
    <div className="flex items-center gap-2">
      {collapsible && (open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
      <Zap className="w-5 h-5 text-neutral-400" />
      <h3 className="font-medium text-neutral-900 dark:text-white">{companyId ? 'AutoPay runs for this company' : 'AutoPay charging'}</h3>
      {settings && (
        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${settings.chargingEnabled ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'}`}>
          {settings.chargingEnabled ? 'Charging on' : 'Preview only'}
        </span>
      )}
      {review.length > 0 && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">{review.length} need review</span>}
    </div>
  );

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
      {collapsible ? (
        <button type="button" className="w-full text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
          {header}
        </button>
      ) : (
        header
      )}
      {open && (
        <div className="mt-4 space-y-5">
          {isLoading && (
            <div className="flex items-center gap-2 text-neutral-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Working out the next run…
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error.response?.data?.error || 'Could not load the AutoPay preview.'}</p>}

          {settings && (
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              {settings.chargingEnabled ? (
                <>AutoPay charges customers&apos; saved payment methods once a day at {String(settings.chargeHour).padStart(2, '0')}:00 ({settings.timezone}). </>
              ) : (
                <>
                  <Eye className="w-4 h-4 inline mr-1 -mt-0.5" />
                  Charging is off: the daily run only works out what it would charge. Nothing below has been or will be charged until an admin turns charging on in{' '}
                  <Link to="/admin/portal-settings#autopay" className="text-primary-600 hover:underline">Portal Settings → Payments</Link>.{' '}
                </>
              )}
              Each company is charged against its own local date, once a day at most.
            </p>
          )}

          {plan && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-500 border-b border-neutral-200 dark:border-neutral-700">
                    {!companyId && <th className="py-2 pr-3 font-medium">Company</th>}
                    <th className="py-2 pr-3 font-medium">Next run</th>
                    <th className="py-2 pr-3 font-medium">Invoices</th>
                    <th className="py-2 pr-3 font-medium text-right">Amount</th>
                    <th className="py-2 pr-3 font-medium">Method</th>
                    <th className="py-2 font-medium">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.companies.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-4 text-neutral-500">No company{companyId ? '' : ' you can see'} has AutoPay turned on.</td>
                    </tr>
                  )}
                  {plan.companies.map((e) => (
                    <tr key={e.companyId} className="border-b border-neutral-100 dark:border-neutral-700/60 align-top">
                      {!companyId && <td className="py-2 pr-3 text-neutral-900 dark:text-white">{e.companyName || `Company ${e.companyId}`}</td>}
                      <td className="py-2 pr-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${ACTION_STYLE[e.action]}`}>{actionLabel(e, plan.chargingEnabled)}</span>
                        <div className="text-xs text-neutral-500 mt-1">their date {formatCalendarDate(e.today)}</div>
                      </td>
                      <td className="py-2 pr-3">
                        {e.invoices.length > 0
                          ? e.invoices.map((l) => (
                              <div key={l.id} className="whitespace-nowrap">
                                {l.number} <span className="text-neutral-500">due {formatCalendarDate(l.dueDate)}</span>
                              </div>
                            ))
                          : e.nextCharge
                            ? <span className="text-neutral-500">next: {formatCalendarDate(e.nextCharge.date)}, {e.nextCharge.invoices.length} invoice(s), {formatMoney(e.nextCharge.amount)}</span>
                            : <span className="text-neutral-400">—</span>}
                        {e.excluded.map((x) => (
                          <div key={`x${x.id}`} className="text-xs text-neutral-500">{x.number}: {x.reasonText}</div>
                        ))}
                      </td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">
                        {e.invoices.length > 0 ? formatMoney(e.amount) : '—'}
                        {e.surcharge > 0 && <div className="text-xs text-neutral-500">incl. {formatMoney(e.surcharge)} surcharge</div>}
                        {e.maxAmount !== null && e.maxAmount !== undefined && <div className="text-xs text-neutral-500">max {formatMoney(e.maxAmount)}</div>}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{methodText(e.method)}</td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">
                        {e.reasonText || (e.action === 'charge' ? 'Due and within limits.' : '')}
                        {e.warning && e.action !== 'skip' && (
                          <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                            <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
                            {e.warning.text}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {plan.totals?.charges > 0 && (
                <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-2">
                  {plan.chargingEnabled ? 'The next run will charge' : 'The next run would charge'} {plan.totals.charges} {plan.totals.charges === 1 ? 'company' : 'companies'}, {formatMoney(plan.totals.amount)} in total.
                </p>
              )}
            </div>
          )}

          {runs.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center gap-1">
                <Clock className="w-4 h-4" /> Recent runs
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.id} className="border-b border-neutral-100 dark:border-neutral-700/60 align-top">
                        <td className="py-2 pr-3 whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                        {!companyId && <td className="py-2 pr-3">Company {r.company_id}</td>}
                        <td className="py-2 pr-3 text-right whitespace-nowrap">{r.amount !== null ? formatMoney(r.amount) : '—'}</td>
                        <td className={`py-2 pr-3 ${RUN_STYLE[r.status] || ''}`}>
                          {r.status === 'succeeded' && <CheckCircle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
                          {RUN_LABEL[r.status] || r.status}
                          {r.reason_detail && <div className="text-xs text-neutral-500">{r.reason_detail}</div>}
                          {r.status === 'succeeded' && !r.cw_applied_at && <div className="text-xs text-amber-600">Not yet applied in ConnectWise</div>}
                        </td>
                        <td className="py-2 text-right">{r.status === 'needs_review' && <ResolveButtons run={r} />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {review.length > 0 && (
                <p className="text-xs text-neutral-500 mt-2">
                  &quot;Needs review&quot; means AutoPay cannot tell whether the charge went through — the process stopped, or the gateway answered with an error rather than a decline. Look it up in the gateway first. Nothing more is charged for that company until it is resolved.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
